import { useState, useMemo, useCallback, useEffect } from "react";
import { diffLines } from "diff";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  FileWarning,
  FileX2,
  FilePlus2,
  ChevronDown,
  ArrowLeftRight,
  Loader2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsDesktop } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ConflictFileData {
  path: string;
  kind: "CONTENT" | "PATH" | "CREATE";
  localContent?: string;
  remoteContent?: string;
  remoteDeleted: boolean;
  localDeleted: boolean;
}

export interface ConflictResolutionProps {
  conflicts: ConflictFileData[];
  initialSelectedPath?: string;
  onResolveFile: (
    path: string,
    params: { strategy: "keep_local" | "keep_remote"; mergedContent?: string },
  ) => Promise<void>;
  onComplete: () => void;
  onCancel: () => void;
  isResolving: boolean;
}

type HunkChoice = "local" | "remote" | "both";

interface DiffSection {
  type: "context" | "change";
  hunkIndex?: number;
  lines: string[];
  localLines?: string[];
  remoteLines?: string[];
}

// ---------------------------------------------------------------------------
// Diff helpers
// ---------------------------------------------------------------------------

const CONTEXT_COLLAPSE_THRESHOLD = 8;

function splitDiffValue(value: string): string[] {
  const trimmed = value.endsWith("\n") ? value.slice(0, -1) : value;
  return trimmed === "" ? [] : trimmed.split("\n");
}

function computeDiffSections(
  local: string,
  remote: string,
): { sections: DiffSection[]; hunkCount: number } {
  if (local === remote) return { sections: [], hunkCount: 0 };

  const changes = diffLines(local, remote);
  const sections: DiffSection[] = [];
  let hunkIndex = 0;
  let i = 0;

  while (i < changes.length) {
    const change = changes[i]!;
    if (!change.added && !change.removed) {
      sections.push({ type: "context", lines: splitDiffValue(change.value) });
      i++;
    } else {
      const localLines: string[] = [];
      const remoteLines: string[] = [];
      while (i < changes.length) {
        const c = changes[i]!;
        if (!c.added && !c.removed) break;
        if (c.removed) {
          localLines.push(...splitDiffValue(c.value));
        } else {
          remoteLines.push(...splitDiffValue(c.value));
        }
        i++;
      }
      sections.push({
        type: "change",
        hunkIndex: hunkIndex++,
        lines: [],
        localLines,
        remoteLines,
      });
    }
  }
  return { sections, hunkCount: hunkIndex };
}

function buildMergedContent(
  sections: DiffSection[],
  choices: Record<number, HunkChoice>,
  originalLocal: string,
): string {
  const result: string[] = [];
  for (const section of sections) {
    if (section.type === "context") {
      result.push(...section.lines);
    } else if (section.type === "change" && section.hunkIndex !== undefined) {
      const choice = choices[section.hunkIndex];
      if (choice === "remote") {
        result.push(...(section.remoteLines ?? []));
      } else if (choice === "both") {
        result.push(...(section.localLines ?? []));
        result.push(...(section.remoteLines ?? []));
      } else {
        result.push(...(section.localLines ?? []));
      }
    }
  }
  let merged = result.join("\n");
  if (originalLocal.endsWith("\n") && !merged.endsWith("\n")) {
    merged += "\n";
  }
  return merged;
}

// ---------------------------------------------------------------------------
// Context lines (with collapse for long unchanged regions)
// ---------------------------------------------------------------------------

function ContextLines({ lines }: { lines: string[] }) {
  const [expanded, setExpanded] = useState(false);

  if (lines.length === 0) return null;

  if (lines.length <= CONTEXT_COLLAPSE_THRESHOLD || expanded) {
    return (
      <>
        {lines.map((line, i) => (
          <div
            key={i}
            className="flex text-xs font-mono leading-6 text-muted-foreground"
          >
            <span className="w-8 shrink-0 select-none text-right pr-2 text-muted-foreground/30">
              &nbsp;
            </span>
            <span className="flex-1 whitespace-pre-wrap px-2">
              {line || "\u200b"}
            </span>
          </div>
        ))}
      </>
    );
  }

  const top = lines.slice(0, 3);
  const bottom = lines.slice(-3);
  const hidden = lines.length - 6;

  return (
    <>
      {top.map((line, i) => (
        <div
          key={`t${i}`}
          className="flex text-xs font-mono leading-6 text-muted-foreground"
        >
          <span className="w-8 shrink-0 select-none text-right pr-2 text-muted-foreground/30">
            &nbsp;
          </span>
          <span className="flex-1 whitespace-pre-wrap px-2">
            {line || "\u200b"}
          </span>
        </div>
      ))}
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="flex w-full items-center justify-center gap-1.5 py-1.5 text-[10px] text-muted-foreground/60 hover:text-foreground hover:bg-muted/50 transition-colors border-y border-dashed border-border/40"
      >
        <ChevronDown className="size-3" />
        {hidden} unchanged line{hidden === 1 ? "" : "s"}
      </button>
      {bottom.map((line, i) => (
        <div
          key={`b${i}`}
          className="flex text-xs font-mono leading-6 text-muted-foreground"
        >
          <span className="w-8 shrink-0 select-none text-right pr-2 text-muted-foreground/30">
            &nbsp;
          </span>
          <span className="flex-1 whitespace-pre-wrap px-2">
            {line || "\u200b"}
          </span>
        </div>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Single change hunk with inline resolution controls
// ---------------------------------------------------------------------------

function HunkView({
  hunkIndex,
  totalHunks,
  localLines,
  remoteLines,
  choice,
  onChoose,
}: {
  hunkIndex: number;
  totalHunks: number;
  localLines: string[];
  remoteLines: string[];
  choice: HunkChoice | null;
  onChoose: (c: HunkChoice) => void;
}) {
  return (
    <div>
      {/* Hunk header */}
      <div className="flex items-center justify-between gap-2 bg-amber-50/80 px-3 py-1.5 dark:bg-amber-950/20 border-y border-amber-200/60 dark:border-amber-800/30">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 tabular-nums">
            Change {hunkIndex + 1}/{totalHunks}
          </span>
          {choice && (
            <Badge variant="secondary" className="text-[10px] py-0 h-4 gap-0.5">
              <Check className="size-2.5" />
              {choice === "both"
                ? "Both"
                : choice === "local"
                  ? "Mine"
                  : "Theirs"}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          {(["local", "remote", "both"] as const).map((opt) => (
            <Button
              key={opt}
              variant={choice === opt ? "default" : "outline"}
              size="xs"
              className={cn(
                "h-5 text-[10px] px-2 font-medium",
                choice !== opt &&
                  opt === "local" &&
                  "border-green-300 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-950/30",
                choice !== opt &&
                  opt === "remote" &&
                  "border-yellow-300 text-yellow-700 hover:bg-yellow-50 dark:border-yellow-700 dark:text-yellow-400 dark:hover:bg-yellow-950/30",
                choice === opt &&
                  opt === "local" &&
                  "bg-green-600 hover:bg-green-700 text-white border-0 shadow-sm",
                choice === opt &&
                  opt === "remote" &&
                  "bg-yellow-600 hover:bg-yellow-700 text-white border-0 shadow-sm",
              )}
              onClick={() => onChoose(opt)}
            >
              {opt === "local"
                ? "Keep Mine"
                : opt === "remote"
                  ? "Keep Theirs"
                  : "Both"}
            </Button>
          ))}
        </div>
      </div>

      {localLines.map((line, i) => (
        <div
          key={`l${i}`}
          className={cn(
            "flex text-xs font-mono leading-6",
            choice === "remote"
              ? "bg-green-50/30 text-muted-foreground/40 line-through dark:bg-green-950/10"
              : "bg-green-50 text-green-900 dark:bg-green-950/30 dark:text-green-200",
          )}
        >
          <span className="w-8 shrink-0 select-none text-center text-green-500/80 dark:text-green-600">
            &minus;
          </span>
          <span className="flex-1 whitespace-pre-wrap px-2">
            {line || "\u200b"}
          </span>
        </div>
      ))}

      {remoteLines.map((line, i) => (
        <div
          key={`r${i}`}
          className={cn(
            "flex text-xs font-mono leading-6",
            choice === "local"
              ? "bg-yellow-50/30 text-muted-foreground/40 line-through dark:bg-yellow-950/10"
              : "bg-yellow-50 text-yellow-900 dark:bg-yellow-950/30 dark:text-yellow-200",
          )}
        >
          <span className="w-8 shrink-0 select-none text-center text-yellow-500/80 dark:text-yellow-600">
            +
          </span>
          <span className="flex-1 whitespace-pre-wrap px-2">
            {line || "\u200b"}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-file diff view (handles CONTENT, CREATE, and PATH/DELETE conflicts)
// ---------------------------------------------------------------------------

function FileDiffView({
  conflict,
  choices,
  pathResolution,
  onChooseHunk,
  onResolvePathConflict,
  onKeepEntireLocal,
  onKeepEntireRemote,
}: {
  conflict: ConflictFileData;
  choices: Record<number, HunkChoice>;
  pathResolution?: "local" | "remote";
  onChooseHunk: (hunkIndex: number, choice: HunkChoice) => void;
  /** For PATH conflicts only: (path, side) => void. Invoked with conflict.path from this component. */
  onResolvePathConflict?: (path: string, side: "local" | "remote") => void;
  onKeepEntireLocal: () => void;
  onKeepEntireRemote: () => void;
}) {
  const isDesktop = useIsDesktop();

  const isPathDeleteConflict = conflict.remoteDeleted || conflict.localDeleted;
  // PATH conflict where one side deleted the file.
  if (isPathDeleteConflict) {
    const deletedByRemote = conflict.remoteDeleted;
    const keepMineChosen = pathResolution === "local";
    const deleteChosen = pathResolution === "remote";
    const contentStruck = deletedByRemote ? deleteChosen : keepMineChosen;
    const resolve = onResolvePathConflict ?? (() => {});

    return (
      <div className="flex flex-1 flex-col min-h-0">
        <div className="flex shrink-0 items-center justify-between gap-2 border-b px-4 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            {isDesktop && (
              <>
                <FileX2 className="size-3.5 shrink-0 text-red-500" />
                <span className="text-sm font-medium truncate">
                  {conflict.path}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              variant={keepMineChosen ? "default" : "outline"}
              size="xs"
              className={cn(
                "border-green-300 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-950/30",
                keepMineChosen &&
                  "bg-green-600 hover:bg-green-700 text-white border-0 shadow-sm",
              )}
              onClick={() => resolve(conflict.path, "local")}
            >
              {deletedByRemote ? "Keep Mine" : "Delete file"}
            </Button>
            <Button
              type="button"
              variant={deleteChosen ? "default" : "outline"}
              size="xs"
              className={cn(
                "border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950/30",
                deleteChosen &&
                  "bg-red-600 hover:bg-red-700 text-white border-0 shadow-sm",
              )}
              onClick={() => resolve(conflict.path, "remote")}
            >
              {deletedByRemote ? "Delete file" : "Restore theirs"}
            </Button>
          </div>
        </div>
        <ScrollArea className="flex-1 min-h-0">
          <div className="p-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              {deletedByRemote
                ? "This file was deleted in the repo but you have changes. Choose to keep your version or accept the deletion."
                : "You deleted this file locally, but it was modified in the repo. Choose to keep your deletion or restore their version."}
            </p>
            {(deletedByRemote
              ? conflict.localContent
              : conflict.remoteContent) && (
              <div
                className={cn(
                  "rounded-lg border overflow-hidden",
                  contentStruck && "opacity-60",
                )}
              >
                <div
                  className={cn(
                    "px-3 py-1.5 border-b text-[10px] font-medium uppercase tracking-wider",
                    deletedByRemote
                      ? "bg-green-50/50 dark:bg-green-950/20 text-green-800 dark:text-green-300"
                      : "bg-yellow-50/50 dark:bg-yellow-950/20 text-yellow-800 dark:text-yellow-300",
                  )}
                >
                  {deletedByRemote ? "Mine" : "Theirs"}
                </div>
                <pre
                  className={cn(
                    "p-3 text-xs font-mono whitespace-pre-wrap leading-6 text-foreground/80 max-h-96 overflow-auto",
                    contentStruck && "line-through",
                  )}
                >
                  {deletedByRemote
                    ? conflict.localContent
                    : conflict.remoteContent}
                </pre>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    );
  }

  // CONTENT or CREATE conflict — show diff
  const local = conflict.localContent ?? "";
  const remote = conflict.remoteContent ?? "";

  if (local === remote) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-sm text-muted-foreground">
        Contents are identical — you can keep either version.
      </div>
    );
  }

  const { sections, hunkCount } = computeDiffSections(local, remote);
  const resolvedInFile = Object.keys(choices).length;
  // I deleted, they modified: local empty, remote has content — "Keep Mine" = delete file
  const isLocalDeleted = remote !== "" && local === "";

  return (
    <div className="flex flex-1 flex-col min-h-0">
      {/* File header */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2">
        <div className="flex items-center gap-2 min-w-0">
          {isDesktop && (
            <>
              {conflict.kind === "CREATE" ? (
                <FilePlus2 className="size-3.5 shrink-0 text-blue-500" />
              ) : (
                <ArrowLeftRight className="size-3.5 shrink-0 text-amber-500" />
              )}
              <span className="text-sm font-medium truncate">
                {conflict.path}
              </span>
            </>
          )}
          <Badge
            variant="outline"
            className="text-[10px] shrink-0 tabular-nums"
          >
            {resolvedInFile}/{hunkCount} resolved
          </Badge>
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="xs"
            className={cn(
              "text-xs",
              isLocalDeleted
                ? "border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950/30"
                : "border-green-300 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-950/30",
            )}
            onClick={onKeepEntireLocal}
          >
            {isLocalDeleted
              ? "Delete file"
              : isDesktop
                ? "Keep All Mine"
                : "All Mine"}
          </Button>
          <Button
            variant="outline"
            size="xs"
            className="text-xs border-yellow-300 text-yellow-700 hover:bg-yellow-50 dark:border-yellow-700 dark:text-yellow-400 dark:hover:bg-yellow-950/30"
            onClick={onKeepEntireRemote}
          >
            {isDesktop ? "Keep All Theirs" : "All Theirs"}
          </Button>
        </div>
      </div>

      {/* Diff content */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="min-w-0">
          {sections.map((section, sIdx) =>
            section.type === "context" ? (
              <ContextLines key={`ctx-${sIdx}`} lines={section.lines} />
            ) : (
              <HunkView
                key={`hunk-${section.hunkIndex}`}
                hunkIndex={section.hunkIndex!}
                totalHunks={hunkCount}
                localLines={section.localLines ?? []}
                remoteLines={section.remoteLines ?? []}
                choice={choices[section.hunkIndex!] ?? null}
                onChoose={(c) => onChooseHunk(section.hunkIndex!, c)}
              />
            ),
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main ConflictResolution component
// ---------------------------------------------------------------------------

export function ConflictResolution({
  conflicts,
  initialSelectedPath,
  onResolveFile,
  onComplete,
  onCancel,
  isResolving,
}: ConflictResolutionProps) {
  const isDesktop = useIsDesktop();
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Per-file, per-hunk choices: { [filePath]: { [hunkIndex]: choice } }
  const [allChoices, setAllChoices] = useState<
    Record<string, Record<number, HunkChoice>>
  >({});
  // File-level resolution for PATH/DELETE conflicts
  const [pathResolutions, setPathResolutions] = useState<
    Record<string, "local" | "remote">
  >({});
  // Tracks which files have already been sent to the backend
  const [appliedFiles, setAppliedFiles] = useState<Set<string>>(new Set());
  const [applyingPath, setApplyingPath] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);

  const selectedConflict = conflicts[selectedIndex];

  useEffect(() => {
    if (!initialSelectedPath) return;
    const idx = conflicts.findIndex((c) => c.path === initialSelectedPath);
    if (idx >= 0) {
      setSelectedIndex(idx);
    }
  }, [initialSelectedPath, conflicts]);

  // Pre-compute diff data per file
  const fileDiffs = useMemo(() => {
    const map: Record<string, { sections: DiffSection[]; hunkCount: number }> =
      {};
    for (const c of conflicts) {
      if (c.remoteDeleted || c.localDeleted) {
        map[c.path] = { sections: [], hunkCount: 1 };
      } else {
        const local = c.localContent ?? "";
        const remote = c.remoteContent ?? "";
        map[c.path] =
          local === remote
            ? { sections: [], hunkCount: 0 }
            : computeDiffSections(local, remote);
      }
    }
    return map;
  }, [conflicts]);

  const isFileResolved = useCallback(
    (c: ConflictFileData): boolean => {
      if (appliedFiles.has(c.path)) return true;
      if (c.remoteDeleted || c.localDeleted)
        return pathResolutions[c.path] !== undefined;
      const diff = fileDiffs[c.path];
      if (!diff || diff.hunkCount === 0) return true;
      const choices = allChoices[c.path] ?? {};
      return Object.keys(choices).length >= diff.hunkCount;
    },
    [allChoices, pathResolutions, fileDiffs, appliedFiles],
  );

  const resolvedCount = conflicts.filter(isFileResolved).length;
  const allResolved = resolvedCount === conflicts.length;
  const currentFileResolved = selectedConflict
    ? isFileResolved(selectedConflict)
    : false;

  /** Find the next unresolved file index (wrapping around), or -1 if none. */
  const nextUnresolvedIndex = useMemo(() => {
    if (allResolved) return -1;
    // Look forward from selectedIndex+1, then wrap
    for (let offset = 1; offset <= conflicts.length; offset++) {
      const idx = (selectedIndex + offset) % conflicts.length;
      if (!isFileResolved(conflicts[idx]!)) return idx;
    }
    return -1;
  }, [selectedIndex, conflicts, allResolved, isFileResolved]);

  const handleNext = useCallback(() => {
    if (nextUnresolvedIndex >= 0) {
      setSelectedIndex(nextUnresolvedIndex);
    }
  }, [nextUnresolvedIndex]);

  // --- handlers ---

  const chooseHunk = useCallback(
    (path: string, hunkIndex: number, choice: HunkChoice) => {
      setAllChoices((prev) => ({
        ...prev,
        [path]: { ...(prev[path] ?? {}), [hunkIndex]: choice },
      }));
    },
    [],
  );

  const keepEntireFile = useCallback(
    (path: string, side: "local" | "remote") => {
      const conflict = conflicts.find((c) => c.path === path);
      if (!conflict) return;
      if (conflict.remoteDeleted || conflict.localDeleted) {
        setPathResolutions((prev) => ({ ...prev, [path]: side }));
        return;
      }
      const diff = fileDiffs[path];
      if (!diff) return;
      const choices: Record<number, HunkChoice> = {};
      for (let i = 0; i < diff.hunkCount; i++) choices[i] = side;
      setAllChoices((prev) => ({ ...prev, [path]: choices }));
    },
    [conflicts, fileDiffs],
  );

  const keepAll = useCallback(
    (side: "local" | "remote") => {
      for (const c of conflicts) keepEntireFile(c.path, side);
    },
    [conflicts, keepEntireFile],
  );

  const handleApplyAll = useCallback(async () => {
    setApplyError(null);
    for (const conflict of conflicts) {
      if (appliedFiles.has(conflict.path)) continue;
      setApplyingPath(conflict.path);
      try {
        if (conflict.remoteDeleted || conflict.localDeleted) {
          const res = pathResolutions[conflict.path];
          await onResolveFile(conflict.path, {
            strategy: res === "remote" ? "keep_remote" : "keep_local",
          });
        } else {
          const diff = fileDiffs[conflict.path];
          const choices = allChoices[conflict.path] ?? {};
          if (diff && diff.hunkCount > 0) {
            const vals = new Set(Object.values(choices));
            if (vals.size === 1 && vals.has("local")) {
              await onResolveFile(conflict.path, { strategy: "keep_local" });
            } else if (vals.size === 1 && vals.has("remote")) {
              await onResolveFile(conflict.path, { strategy: "keep_remote" });
            } else {
              const merged = buildMergedContent(
                diff.sections,
                choices,
                conflict.localContent ?? "",
              );
              await onResolveFile(conflict.path, {
                strategy: "keep_local",
                mergedContent: merged,
              });
            }
          } else {
            await onResolveFile(conflict.path, { strategy: "keep_local" });
          }
        }
        setAppliedFiles((prev) => new Set([...prev, conflict.path]));
      } catch (err) {
        setApplyError(err instanceof Error ? err.message : "Resolution failed");
        setApplyingPath(null);
        return;
      }
    }
    setApplyingPath(null);
    onComplete();
  }, [
    conflicts,
    appliedFiles,
    pathResolutions,
    fileDiffs,
    allChoices,
    onResolveFile,
    onComplete,
  ]);

  // --- empty state ---

  if (conflicts.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        No conflicts to resolve.
      </div>
    );
  }

  // --- render ---

  return (
    <div className="flex h-full flex-col min-h-0 overflow-hidden bg-background">
      {/* ---- Progress banner ---- */}
      <div
        className={cn(
          "flex items-center gap-2 border-b px-4 py-2",
          allResolved
            ? "bg-green-50 dark:bg-green-950/30 dark:border-green-900/40"
            : "bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900/40",
        )}
      >
        {allResolved ? (
          <Check className="size-4 shrink-0 text-green-600 dark:text-green-400" />
        ) : (
          <AlertTriangle className="size-4 shrink-0 text-amber-600 dark:text-amber-400" />
        )}
        <p
          className={cn(
            "flex-1 text-sm",
            allResolved
              ? "text-green-800 dark:text-green-200"
              : "text-amber-800 dark:text-amber-200",
          )}
        >
          {allResolved ? (
            <>
              All conflicts resolved.{" "}
              <span className="font-medium">
                Click &ldquo;Apply Resolutions&rdquo; to finish.
              </span>
            </>
          ) : (
            <>
              Some conflicts need resolved.{" "}
              <span className="font-medium tabular-nums">
                {conflicts.length - resolvedCount} file
                {conflicts.length - resolvedCount === 1 ? "" : "s"} remaining.
              </span>
            </>
          )}
        </p>
        {applyError && (
          <span className="text-xs text-destructive shrink-0">
            {applyError}
          </span>
        )}
      </div>

      {/* ---- Main area ---- */}
      <div
        className={cn(
          "flex min-h-0 flex-1",
          isDesktop ? "flex-row" : "flex-col",
        )}
      >
        {/* -- File list -- */}
        {isDesktop ? (
          <aside className="flex w-56 shrink-0 flex-col border-r">
            <div className="px-3 py-2.5">
              <h2 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                Conflicted files ({conflicts.length})
              </h2>
            </div>
            <ScrollArea className="flex-1">
              <div className="space-y-0.5 px-1.5 pb-3">
                {conflicts.map((c, idx) => {
                  const resolved = isFileResolved(c);
                  const applied = appliedFiles.has(c.path);
                  const Icon = c.remoteDeleted
                    ? FileX2
                    : c.localDeleted
                      ? FilePlus2
                      : c.kind === "CREATE"
                        ? FilePlus2
                        : FileWarning;
                  return (
                    <button
                      key={c.path}
                      type="button"
                      onClick={() => setSelectedIndex(idx)}
                      className={cn(
                        "flex w-full items-start gap-2 rounded-md px-2 py-2 text-left transition-colors",
                        idx === selectedIndex
                          ? "bg-accent"
                          : "hover:bg-accent/50",
                      )}
                    >
                      <Icon
                        className={cn(
                          "mt-0.5 size-3.5 shrink-0",
                          resolved
                            ? "text-green-600 dark:text-green-400"
                            : c.remoteDeleted
                              ? "text-red-500"
                              : c.localDeleted
                                ? "text-yellow-500"
                                : c.kind === "CREATE"
                                  ? "text-blue-500"
                                  : "text-amber-500",
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <p className="truncate text-xs font-medium flex-1">
                            {c.path.split("/").pop()}
                          </p>
                          <span
                            className={cn(
                              "size-2 shrink-0 rounded-full",
                              applied || resolved
                                ? "bg-green-500"
                                : "bg-red-500",
                            )}
                          />
                        </div>
                        <p className="truncate text-[10px] text-muted-foreground">
                          {c.path.includes("/")
                            ? c.path.slice(0, c.path.lastIndexOf("/"))
                            : "/"}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
            <div className="border-t p-2 space-y-1.5">
              <Button
                variant="default"
                size="sm"
                className="w-full text-xs bg-green-600 hover:bg-green-700 text-white border-0"
                onClick={() => keepAll("local")}
                disabled={isResolving}
              >
                Keep all mine
              </Button>
              <Button
                variant="default"
                size="sm"
                className="w-full text-xs bg-yellow-600 hover:bg-yellow-700 text-white border-0"
                onClick={() => keepAll("remote")}
                disabled={isResolving}
              >
                Keep all theirs
              </Button>
            </div>
          </aside>
        ) : (
          /* Mobile: tab bar + diff view */
          <Tabs
            value={selectedConflict?.path ?? conflicts[0]?.path ?? ""}
            onValueChange={(value) => {
              const idx = conflicts.findIndex((c) => c.path === value);
              if (idx >= 0) setSelectedIndex(idx);
            }}
            className="flex flex-1 flex-col min-h-0 min-w-0"
          >
            <div className="shrink-0 border-b px-3 pt-2">
              <TabsList variant="line">
                {conflicts.map((c) => {
                  const resolved = isFileResolved(c);
                  return (
                    <TabsTrigger key={c.path} value={c.path}>
                      <span
                        className={cn(
                          "size-1.5 shrink-0 rounded-full",
                          resolved ? "bg-green-500" : "bg-red-500",
                        )}
                      />
                      {c.path.split("/").pop()}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>
            {conflicts.map((c) => (
              <TabsContent
                key={c.path}
                value={c.path}
                className="mt-0 flex flex-1 flex-col min-h-0 data-[state=inactive]:hidden"
              >
                <FileDiffView
                  conflict={c}
                  choices={allChoices[c.path] ?? {}}
                  pathResolution={
                    c.remoteDeleted || c.localDeleted
                      ? pathResolutions[c.path]
                      : undefined
                  }
                  onResolvePathConflict={
                    c.remoteDeleted || c.localDeleted
                      ? keepEntireFile
                      : undefined
                  }
                  onChooseHunk={(idx, choice) => {
                    chooseHunk(c.path, idx, choice);
                  }}
                  onKeepEntireLocal={() => {
                    keepEntireFile(c.path, "local");
                  }}
                  onKeepEntireRemote={() => {
                    keepEntireFile(c.path, "remote");
                  }}
                />
              </TabsContent>
            ))}
          </Tabs>
        )}

        {/* -- Diff view (desktop only) -- */}
        {isDesktop && selectedConflict && (
          <div className="flex flex-1 flex-col min-h-0 min-w-0">
            <FileDiffView
              conflict={selectedConflict}
              choices={allChoices[selectedConflict.path] ?? {}}
              pathResolution={
                selectedConflict.remoteDeleted || selectedConflict.localDeleted
                  ? pathResolutions[selectedConflict.path]
                  : undefined
              }
              onResolvePathConflict={
                selectedConflict.remoteDeleted || selectedConflict.localDeleted
                  ? keepEntireFile
                  : undefined
              }
              onChooseHunk={(idx, choice) => {
                chooseHunk(selectedConflict.path, idx, choice);
              }}
              onKeepEntireLocal={() => {
                keepEntireFile(selectedConflict.path, "local");
              }}
              onKeepEntireRemote={() => {
                keepEntireFile(selectedConflict.path, "remote");
              }}
            />
          </div>
        )}
      </div>

      {/* ---- Bottom bar ---- */}
      <div className="flex items-center justify-between border-t px-4 py-2.5">
        <button
          type="button"
          onClick={onCancel}
          disabled={isResolving}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors disabled:opacity-50"
        >
          <ArrowLeft className="size-3" />
          Back to editor
        </button>
        <div className="flex items-center gap-3">
          {applyingPath && (
            <span className="text-xs text-muted-foreground animate-pulse truncate max-w-48">
              Applying {applyingPath.split("/").pop()}&hellip;
            </span>
          )}
          {allResolved ? (
            <Button size="sm" disabled={isResolving} onClick={handleApplyAll}>
              {isResolving ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" />
                  Applying&hellip;
                </>
              ) : (
                <>
                  <Check className="size-3.5" />
                  Apply Resolutions
                </>
              )}
            </Button>
          ) : (
            <Button
              size="sm"
              disabled={!currentFileResolved || isResolving}
              onClick={handleNext}
            >
              Next
              <ArrowRight className="size-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
